import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Building2, Send, Mail, Globe, Phone, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function CompaniesPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "master";

  const [companies, setCompanies] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openSend, setOpenSend] = useState(false);
  const [newCo, setNewCo] = useState({ name: "", email: "", phone: "", website: "" });
  const [sendForm, setSendForm] = useState({ client_ids: [], subject: "", message: "" });

  const loadCompanies = async () => {
    const res = await api.get("/companies");
    setCompanies(res.data);
  };
  useEffect(() => { loadCompanies(); }, []);

  useEffect(() => {
    if (!selectedId) { setDetails(null); return; }
    setLoading(true);
    api.get(`/companies/${selectedId}/clients`).then(r => setDetails(r.data)).finally(() => setLoading(false));
  }, [selectedId]);

  const addCompany = async () => {
    if (!newCo.name) return;
    await api.post("/companies", newCo);
    toast.success("Gesellschaft hinzugefügt");
    setOpenAdd(false);
    setNewCo({ name: "", email: "", phone: "", website: "" });
    loadCompanies();
  };

  const deleteCompany = async (id) => {
    if (!window.confirm(t.confirm_delete)) return;
    await api.delete(`/companies/${id}`);
    toast.success("Gelöscht");
    if (selectedId === id) setSelectedId("");
    loadCompanies();
  };

  const sendDocs = async () => {
    if (!selectedId || sendForm.client_ids.length === 0) return;
    try {
      const res = await api.post(`/companies/${selectedId}/send`, sendForm);
      toast.success(res.data.note || "Gesendet");
      setOpenSend(false);
      setSendForm({ client_ids: [], subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const toggleClient = (cid) => {
    setSendForm(s => ({
      ...s,
      client_ids: s.client_ids.includes(cid)
        ? s.client_ids.filter(x => x !== cid)
        : [...s.client_ids, cid],
    }));
  };

  return (
    <div className="space-y-6" data-testid="companies-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-uppercase text-prime">/ {t.companies}</div>
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight mt-1">Gesellschaften</h1>
          <p className="text-sm text-muted-foreground mt-2">Gesellschaften verwalten und Kundendokumente per E-Mail senden.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger className="w-64" data-testid="company-select">
              <SelectValue placeholder="Gesellschaft auswählen…" />
            </SelectTrigger>
            <SelectContent>
              {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {isAdmin && (
            <Button variant="outline" className="gap-2" onClick={() => setOpenAdd(true)} data-testid="add-company-btn">
              <Plus className="w-4 h-4" /> {t.add_company}
            </Button>
          )}
        </div>
      </div>

      {!selectedId ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {companies.map(c => (
            <div
              key={c.id}
              onClick={() => setSelectedId(c.id)}
              className="prime-card p-4 cursor-pointer flex flex-col items-start gap-3"
              data-testid={`company-card-${c.id}`}
            >
              <div className="w-10 h-10 bg-prime/10 text-prime rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold truncate">{c.name}</div>
                <div className="text-xs text-muted-foreground font-mono truncate">{c.email || "—"}</div>
              </div>
            </div>
          ))}
        </div>
      ) : details ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Company card */}
          <div className="prime-card p-6 lg:col-span-1" data-testid="company-detail">
            <div className="w-20 h-20 rounded-lg overflow-hidden bg-card border border-border flex items-center justify-center mb-4">
              {details.company.logo_url ? (
                <img src={details.company.logo_url} alt={details.company.name} className="w-full h-full object-contain p-2"
                  onError={(e) => { e.currentTarget.style.display='none'; e.currentTarget.nextElementSibling.style.display='flex'; }} />
              ) : null}
              <div className="w-full h-full bg-prime/10 text-prime items-center justify-center"
                style={{ display: details.company.logo_url ? 'none' : 'flex' }}>
                <Building2 className="w-9 h-9" strokeWidth={1.5} />
              </div>
            </div>
            <h2 className="font-display font-extrabold text-2xl">{details.company.name}</h2>
            <div className="space-y-2 mt-4 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="w-3.5 h-3.5" /> <span className="font-mono">{details.company.email || "—"}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Phone className="w-3.5 h-3.5" /> <span className="font-mono">{details.company.phone || "—"}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Globe className="w-3.5 h-3.5" /> <span className="font-mono">{details.company.website || "—"}</span>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-border grid grid-cols-2 gap-3">
              <div>
                <div className="label-uppercase text-muted-foreground">Kunden</div>
                <div className="font-display font-bold text-2xl">{details.items.length}</div>
              </div>
              <div>
                <div className="label-uppercase text-muted-foreground">Policen</div>
                <div className="font-display font-bold text-2xl">
                  {details.items.reduce((a, x) => a + x.policies.length, 0)}
                </div>
              </div>
            </div>
            <div className="mt-6 space-y-2">
              <Button
                className="btn-prime w-full gap-2"
                disabled={details.items.length === 0}
                onClick={() => {
                  setSendForm({
                    client_ids: details.items.map(x => x.client.id),
                    subject: `Dokumente Kunden — ${details.company.name}`,
                    message: `Sehr geehrte/r ${details.company.name},\n\nin beigelegt i Dokumente dei nostri Kunden assicurati con voi.\n\nMit freundlichen Grüssen,\nWertia Finanz GmbH`,
                  });
                  setOpenSend(true);
                }}
                data-testid="send-docs-btn"
              >
                <Send className="w-4 h-4" /> {t.send_to_company}
              </Button>
              {isAdmin && (
                <Button variant="outline" className="w-full gap-2 text-destructive" onClick={() => deleteCompany(details.company.id)} data-testid="delete-company-btn">
                  <Trash2 className="w-3.5 h-3.5" /> {t.delete}
                </Button>
              )}
            </div>
          </div>

          {/* Clients */}
          <div className="lg:col-span-2 prime-card" data-testid="company-clients-list">
            <div className="px-6 py-4 border-b border-border">
              <div className="label-uppercase text-muted-foreground">Portafoglio</div>
              <h3 className="font-display font-bold text-xl">Kunden con Policen {details.company.name}</h3>
            </div>
            {details.items.length === 0 ? (
              <div className="p-12 text-center text-sm text-muted-foreground">—</div>
            ) : (
              <div className="divide-y divide-border">
                {details.items.map(({ client, policies }) => (
                  <div key={client.id} className="px-6 py-4 flex items-center gap-4 hover:bg-secondary" data-testid={`co-client-${client.id}`}>
                    <button onClick={() => navigate(`/clients/${client.id}`)} className="flex items-center gap-4 flex-1 text-left">
                      <div className="w-9 h-9 rounded-full bg-foreground text-background flex items-center justify-center font-display text-xs">
                        {(client.first_name?.[0] || "")}{(client.last_name?.[0] || "")}
                      </div>
                      <div>
                        <div className="font-medium text-sm">{client.first_name} {client.last_name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{policies.length} Polic{policies.length === 1 ? "a" : "e"}</div>
                      </div>
                    </button>
                    <div className="flex flex-wrap gap-1">
                      {policies.map(p => (
                        <span key={p.id} className="text-[10px] uppercase tracking-wider px-2 py-1 bg-prime/10 text-prime rounded">
                          {p.insurance_type}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : null}

      {/* Add company */}
      <Dialog open={openAdd} onOpenChange={setOpenAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.add_company}</DialogTitle>
            <DialogDescription>Hinzufügen una nuova Gesellschaft assicurativa.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-uppercase text-muted-foreground">Vorname</Label>
              <Input value={newCo.name} onChange={e => setNewCo({ ...newCo, name: e.target.value })} className="mt-1" data-testid="co-name" />
            </div>
            <div>
              <Label className="label-uppercase text-muted-foreground">E-Mail</Label>
              <Input value={newCo.email} onChange={e => setNewCo({ ...newCo, email: e.target.value })} className="mt-1" data-testid="co-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-uppercase text-muted-foreground">Telefon</Label>
                <Input value={newCo.phone} onChange={e => setNewCo({ ...newCo, phone: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="label-uppercase text-muted-foreground">Website</Label>
                <Input value={newCo.website} onChange={e => setNewCo({ ...newCo, website: e.target.value })} className="mt-1" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenAdd(false)}>{t.cancel}</Button>
            <Button className="btn-prime" onClick={addCompany} data-testid="save-company-btn">{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send dialog */}
      <Dialog open={openSend} onOpenChange={setOpenSend}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t.send_to_company}: {details?.company.name}</DialogTitle>
            <DialogDescription>
              E-Mail integrazione pending Resend API key. La richiesta verrà registrata per audit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-auto">
            <div>
              <Label className="label-uppercase text-muted-foreground">Destinatario</Label>
              <Input value={details?.company.email || ""} disabled className="mt-1 font-mono text-sm" />
            </div>
            <div>
              <Label className="label-uppercase text-muted-foreground">Oggetto</Label>
              <Input value={sendForm.subject} onChange={e => setSendForm({ ...sendForm, subject: e.target.value })} className="mt-1" data-testid="send-subject" />
            </div>
            <div>
              <Label className="label-uppercase text-muted-foreground">Messaggio</Label>
              <Textarea value={sendForm.message} onChange={e => setSendForm({ ...sendForm, message: e.target.value })} rows={5} className="mt-1" data-testid="send-message" />
            </div>
            <div>
              <Label className="label-uppercase text-muted-foreground">Kunden inclusi ({sendForm.client_ids.length})</Label>
              <div className="mt-2 border border-border rounded max-h-40 overflow-auto divide-y divide-border">
                {details?.items.map(({ client }) => (
                  <label key={client.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-secondary">
                    <input
                      type="checkbox"
                      checked={sendForm.client_ids.includes(client.id)}
                      onChange={() => toggleClient(client.id)}
                      data-testid={`send-toggle-${client.id}`}
                    />
                    <span>{client.first_name} {client.last_name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenSend(false)}>{t.cancel}</Button>
            <Button className="btn-prime gap-2" onClick={sendDocs} disabled={sendForm.client_ids.length === 0} data-testid="confirm-send-btn">
              <Send className="w-4 h-4" /> Senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
